import { Exercise as ComponentExercise, ExerciseType, VideoData } from "@/services/admin/exercises.interfaces";
import { EditorInterface } from "./EditorInterface";
import { Video as VideoIcon } from "lucide-react";
import { ExerciseType as InternalExerciseType } from "./types";

/**
 * Editor para exercícios de assistir vídeo (versão unificada)
 * Especialização dos exercícios de vídeo com foco na experiência de apenas assistir
 */
export class WatchVideoInfo extends EditorInterface {
  /**
   * Retorna as informações para exibir no cartão do exercício
   * @param exercise Objeto do exercício
   */
  static GetCardInfo(exercise: ComponentExercise): {
    title: string;
    subtitle: string;
    description?: string;
  } {
    if (!exercise || !WatchVideoInfo.SupportsExerciseType(exercise.type as unknown as string)) {
      return {
        title: 'Assistir Vídeo',
        subtitle: 'Sem detalhes',
      };
    }

    try {
      // Tenta extrair os dados do exercício, lidando com diferentes formatos
      let videoUrl = '';
      let title = 'Sem título';
      let instructions = '';
      
      // Se os dados estiverem no formato aninhado (data é um objeto)
      if (exercise.data && typeof exercise.data === 'object') {
        const data = exercise.data as unknown as VideoData;
        videoUrl = data.videoUrl || data.video || '';
        title = data.title || 'Sem título';
        instructions = data.instructions || '';
      } 
      // Se os dados estiverem no formato plano
      else {
        videoUrl = (exercise as any).videoUrl || (exercise as any).video || '';
        title = (exercise as any).title || 'Sem título';
        instructions = (exercise as any).instructions || '';
      }
      
      // Formata o subtitle para exibir o título do vídeo
      const subtitle = title.length > 0 ? title : 'Sem título';
      
      // Formata a descrição para exibir a URL e instruções, se existirem
      let cardDescription = '';
      
      if (videoUrl) {
        // Extrai apenas o ID ou nome do vídeo para uma exibição mais limpa
        const videoId = WatchVideoInfo.extractVideoIdentifier(videoUrl);
        cardDescription += `Vídeo: ${videoId || videoUrl}`;
      }
      
      if (instructions) {
        if (cardDescription) cardDescription += ' | ';
        cardDescription += instructions.length > 80 
          ? `${instructions.substring(0, 80)}...` 
          : instructions;
      }
      
      return {
        title: "Assistir Vídeo",
        subtitle,
        description: cardDescription || undefined,
      };
    } catch (error) {
      console.error("Erro ao processar dados do exercício de assistir vídeo:", error);
      return {
        title: 'Assistir Vídeo',
        subtitle: 'Erro ao processar dados',
      };
    }
  }
  
  /**
   * Extrai um identificador mais amigável de uma URL de vídeo
   * @param url URL do vídeo
   */
  private static extractVideoIdentifier(url: string): string {
    try {
      // Para URLs do YouTube
      const youtubeMatch = url.match(/(?:v=|\/)([\w-]{11})(?:\?|&|\/|$)/);
      if (youtubeMatch) return `YouTube: ${youtubeMatch[1]}`;
      
      // Para URLs do Vimeo
      const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
      if (vimeoMatch) return `Vimeo: ${vimeoMatch[1]}`;
      
      // Se não for possível identificar, retorna uma versão resumida da URL
      return url.length > 30 ? `${url.substring(0, 30)}...` : url;
    } catch {
      return url;
    }
  }
  
  /**
   * Retorna o título do tipo de exercício
   */
  static getTitle(): string {
    return "Assistir Vídeo";
  }
  
  /**
   * Retorna uma descrição formatada para o exercício
   * @param exercise Objeto do exercício
   */
  static getDescription(exercise: ComponentExercise): string {
    if (!exercise) {
      return "Sem detalhes";
    }
    
    try {
      // Tenta extrair a URL do vídeo e instruções do exercício
      let videoUrl = '';
      let instructions = '';
      
      // Se os dados estiverem no formato aninhado (data é um objeto)
      if (exercise.data && typeof exercise.data === 'object') {
        const data = exercise.data as unknown as VideoData;
        videoUrl = data.videoUrl || data.video || '';
        instructions = data.instructions || '';
      } 
      // Se os dados estiverem no formato plano
      else {
        videoUrl = (exercise as any).videoUrl || (exercise as any).video || '';
        instructions = (exercise as any).instructions || '';
      }
      
      if (instructions) {
        return instructions.length > 100 
          ? `${instructions.substring(0, 100)}...` 
          : instructions;
      }
      
      return videoUrl 
        ? `Vídeo: ${WatchVideoInfo.extractVideoIdentifier(videoUrl)}` 
        : "Sem vídeo definido";
    } catch (error) {
      console.error("Erro ao processar dados do exercício:", error);
      return "Erro ao processar dados";
    }
  }
  
  /**
   * Retorna o ícone para o tipo de exercício
   */
  static getIcon() {
    return VideoIcon;
  }
  
  /**
   * Verifica se o exercício é do tipo Assistir Vídeo
   */
  static SupportsExerciseType(type: string): boolean {
    return type === InternalExerciseType.WatchVideo || type === InternalExerciseType.WatchLargeVideo;
  }
}